import { useState, useEffect, useCallback, useRef } from 'react'
import { conflictsApi, type ConflictLogListResponse } from '@/services/api/conflicts'
import type { ConflictLogItem, ConflictConfigSummary } from '@/types/admin5'
import { extractErrorMessage } from '@/utils/errorHandler'
import { useToast } from '@/components/ui/ToastProvider'
import { getStatusColor, getStatusLabel } from '@/utils/statusHelpers'
import { RefreshCw, AlertTriangle, ScanLine, Check } from 'lucide-react'

type FilterTab = 'all' | 'teacher_overlap' | 'substitution_double_booking' | 'other'

export function ConflictManagementPage() {
  const { showToast } = useToast()
  const [logs, setLogs] = useState<ConflictLogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterTab>('all')
  const [resolveTarget, setResolveTarget] = useState<ConflictLogItem | null>(null)
  const [resolveMethod, setResolveMethod] = useState('')
  const [resolveNotes, setResolveNotes] = useState('')
  const [resolving, setResolving] = useState(false)
  const [config, setConfig] = useState<ConflictConfigSummary | null>(null)
  const [showConfig, setShowConfig] = useState(false)
  const initRef = useRef(false)

  const fetchLogs = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true)
    try {
      const res: ConflictLogListResponse = await conflictsApi.listConflicts({ page_size: 100 })
      setLogs(res.logs || [])
      setError(null)
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Konflikte konnten nicht geladen werden.' }))
    } finally {
      setLoading(false); setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    if (initRef.current) { fetchLogs(true) } else { initRef.current = true; fetchLogs() }
  }, [fetchLogs])

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fetchLogs(true)
    }, 60000)
    return () => clearInterval(id)
  }, [fetchLogs])

  useEffect(() => {
    conflictsApi.getConfig().then(setConfig).catch(() => {})
  }, [])

  const handleScan = async () => {
    setScanning(true)
    try {
      const res = await conflictsApi.bulkScan()
      showToast({ message: `Scan abgeschlossen: ${res.conflicts_found ?? res.total_conflicts_found ?? 0} Konflikte gefunden.`, type: 'success' })
      fetchLogs(true)
    } catch (err) {
      showToast({ message: extractErrorMessage(err, { loadError: 'Scan fehlgeschlagen.' }), type: 'error' })
    } finally { setScanning(false) }
  }

  const handleResolve = async () => {
    if (!resolveTarget) return
    if (!resolveMethod.trim()) {
      showToast({ message: 'Lösungsmethode erforderlich.', type: 'error' })
      return
    }
    setResolving(true)
    try {
      const res = await conflictsApi.resolveConflict(resolveTarget.id, resolveMethod.trim(), resolveNotes.trim() || undefined)
      if ((res as Record<string, unknown>).error === 'Already resolved') {
        showToast({ message: 'Konflikt wurde bereits gelöst.', type: 'info' })
      } else {
        showToast({ message: 'Konflikt als gelöst markiert.', type: 'success' })
      }
      setResolveTarget(null); setResolveMethod(''); setResolveNotes('')
      fetchLogs(true)
    } catch (err) {
      showToast({ message: extractErrorMessage(err, { loadError: 'Konnte Konflikt nicht lösen.' }), type: 'error' })
    } finally { setResolving(false) }
  }

  const filtered = logs.filter(l => {
    if (l.is_resolved) return false
    if (filter === 'all') return true
    if (filter === 'teacher_overlap') return l.conflict_type === 'teacher_overlap'
    if (filter === 'substitution_double_booking') return l.conflict_type === 'substitution_double_booking'
    if (filter === 'other') return !['teacher_overlap', 'substitution_double_booking'].includes(l.conflict_type)
    return true
  })

  const openCount = logs.filter(l => !l.is_resolved).length
  const resolvedCount = logs.filter(l => l.is_resolved).length
  const teacherOverlapCount = logs.filter(l => !l.is_resolved && l.conflict_type === 'teacher_overlap').length
  const subCollisionCount = logs.filter(l => !l.is_resolved && l.conflict_type === 'substitution_double_booking').length

  if (loading) return <div className="p-6 text-gray-500">Laden...</div>

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Konflikt-Management</h1>
          <p className="text-sm text-gray-500 mt-1">Doppelbuchungen & Kollisionen systematisch erkennen und lösen</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowConfig(s => !s)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            Konfiguration
          </button>
          <button onClick={() => fetchLogs(true)} disabled={refreshing}
            className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
          <button onClick={handleScan} disabled={scanning}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
            Scan starten
          </button>
        </div>
      </div>

      {showConfig && config && (
        <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Konflikt-Erkennung Konfiguration</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-gray-500">Lehrer-Konflikte:</span> <span className={config.detect_teacher_conflicts ? 'text-green-600' : 'text-gray-400'}>{config.detect_teacher_conflicts ? 'An' : 'Aus'}</span></div>
            <div><span className="text-gray-500">Raum-Konflikte:</span> <span className={config.detect_room_conflicts ? 'text-green-600' : 'text-gray-400'}>{config.detect_room_conflicts ? 'An' : 'Aus'}</span></div>
            <div><span className="text-gray-500">Klassen-Konflikte:</span> <span className={config.detect_class_conflicts ? 'text-green-600' : 'text-gray-400'}>{config.detect_class_conflicts ? 'An' : 'Aus'}</span></div>
            <div><span className="text-gray-500">Vertretungs-Konflikte:</span> <span className={config.detect_substitution_conflicts !== false ? 'text-green-600' : 'text-gray-400'}>{config.detect_substitution_conflicts !== false ? 'An' : 'Aus'}</span></div>
          </div>
        </div>
      )}

      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Offene Konflikte</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{openCount}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Doppelbuchungen</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{teacherOverlapCount}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Vertretungs-Kollisionen</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{subCollisionCount}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Gelöst</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{resolvedCount}</div>
        </div>
      </div>

      <div className="flex gap-1 mb-4 flex-wrap">
        {([
          { key: 'all', label: `Alle (${openCount})` },
          { key: 'teacher_overlap', label: `Doppelbuchungen (${teacherOverlapCount})` },
          { key: 'substitution_double_booking', label: `Vertretungs-Kollisionen (${subCollisionCount})` },
          { key: 'other', label: 'Sonstige' },
        ] as Array<{ key: FilterTab; label: string }>).map(tab => (
          <button key={tab.key} onClick={() => setFilter(tab.key)}
            className={`px-3 py-1.5 text-xs rounded-md ${filter === tab.key ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p>Keine offenen Konflikte</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(log => (
            <div key={log.id} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${log.severity === 'error' ? 'bg-red-50' : 'bg-amber-50'}`}>
                  <AlertTriangle className={`w-5 h-5 ${log.severity === 'error' ? 'text-red-600' : 'text-amber-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 text-sm">{log.teacher_name || 'Unbekannter Lehrer'}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(log.conflict_type)}`}>
                      {getStatusLabel(log.conflict_type)}
                    </span>
                    <span className={`px-1.5 py-0.5 text-xs rounded ${log.severity === 'error' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}>
                      {log.severity === 'error' ? 'Kritisch' : 'Warnung'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {log.date && <span>Datum: {log.date}</span>}
                    {log.detected_at && <span className="ml-3">Erkannt: {new Date(log.detected_at).toLocaleDateString('de-DE')}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={() => { setResolveTarget(log); setResolveMethod(''); setResolveNotes('') }}
                      className="text-xs text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded">
                      Konflikt lösen
                    </button>
                    <button onClick={() => {
                      conflictsApi.resolveConflict(log.id, 'Behoben').then(() => { showToast({ message: 'Als gelöst markiert.', type: 'success' }); fetchLogs(true) }).catch(() => {})
                    }}
                      className="text-xs text-gray-600 border border-gray-300 hover:bg-gray-50 px-3 py-1.5 rounded flex items-center gap-1">
                      <Check className="w-3 h-3" /> Als gelöst markieren
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filtered.length > 0 && logs.some(l => l.is_resolved) && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Kürzlich gelöst</h3>
          <div className="space-y-2">
            {logs.filter(l => l.is_resolved).slice(0, 5).map(log => (
              <div key={log.id} className="flex items-center justify-between bg-white rounded-lg border border-gray-100 px-4 py-2">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium">{log.teacher_name || 'Unbekannt'}</span>
                    <span className="ml-2 text-xs text-gray-500">{getStatusLabel(log.conflict_type)}</span>
                  </div>
                </div>
                <span className="text-xs text-gray-400">{log.resolution_method || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {resolveTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResolveTarget(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-4">Konflikt lösen</h2>
            <p className="text-sm text-gray-600 mb-4">
              {resolveTarget.teacher_name || 'Unbekannt'} · {getStatusLabel(resolveTarget.conflict_type)}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lösungsmethode <span className="text-red-500">*</span></label>
            <input value={resolveMethod} onChange={e => setResolveMethod(e.target.value.slice(0, 30))}
              maxLength={30} placeholder="z.B. Neu zugewiesen"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-1 text-sm" />
            <p className="text-xs text-gray-400 mb-3">{resolveMethod.length}/30 Zeichen</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notiz (optional)</label>
            <textarea value={resolveNotes} onChange={e => setResolveNotes(e.target.value)}
              rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 text-sm" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setResolveTarget(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Abbrechen</button>
              <button onClick={handleResolve} disabled={resolving || !resolveMethod.trim()}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {resolving ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
